import React, {useEffect, useMemo, useState} from 'react';
import {MultiSelect} from 'primereact/multiselect';
import {InputText} from 'primereact/inputtext';
import {debounce} from 'lodash-es';
import {useTranslation} from 'react-i18next';
import {FilterOption} from '../../../common/types/FilterOption';

const FILTER_DEBOUNCE_MS = 300;

interface FilterMultiSelectProps {
  value: string[];
  options: FilterOption[];
  onChange: (value: string[]) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  inline?: boolean;
  className?: string;
  width?: number;
  showFilter?: boolean;
}

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  value,
  options,
  onChange,
  onKeyDown,
  className = "filter-multiselect",
                                                               width = 400,
                                                               showFilter = false
}) => {
  const {t} = useTranslation();
  const [filterInputValue, setFilterInputValue] = useState('');
  const [filterText, setFilterText] = useState('');

  const debouncedSetFilterText = useMemo(() => {
    return debounce((value: string) => {
      setFilterText(value);
    }, FILTER_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSetFilterText.cancel();
    };
  }, [debouncedSetFilterText]);

  const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFilterInputValue(newValue);
    debouncedSetFilterText(newValue);
  };

  const filteredOptions = useMemo(() => {
    if (!showFilter || !filterText.trim()) {
      return options;
    }
    const lowerFilter = filterText.toLowerCase();
    return options.filter(option =>
      option.label.toLowerCase().includes(lowerFilter)
    );
  }, [options, filterText, showFilter]);

  return (
    <div className={className}>
      {showFilter && (
        <div className="px-4 mb-2">
          <InputText
            value={filterInputValue}
            onChange={handleFilterInputChange}
            placeholder={t('gallery.filters.searchPlaceholder')}
            className="w-full"
          />
        </div>
      )}
      <MultiSelect
        value={value}
        options={filteredOptions}
        onChange={(e) => onChange(e.value)}
        onKeyDown={onKeyDown}
        optionLabel="label"
        optionValue="value"
        className=""
        showSelectAll={false}
        filter={false}
        inline={true}
        display="comma"
        checkboxIcon="fa fa-check"
        emptyMessage=" "
        scrollHeight="400px"
        virtualScrollerOptions={{ itemSize: 35, autoSize: true, orientation: 'vertical' }}
        pt={{
          root: {
            style: {width: `${width}px`}
          }
        }}
      />
    </div>
  );
};

export default FilterMultiSelect;
